import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { payments as paymentsApi } from '../api/endpoints';
import { useOrderEvents } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Pagination, StatCard, money, dateTime } from '../components/Common';

export default function Payments() {
  const toast = useToast();

  const [tab, setTab] = useState('collected');
  const [data, setData] = useState({ payments: [], totals: [], meta: null });
  const [due, setDue] = useState({ orders: [], totalDue: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const [list, outstanding] = await Promise.all([
        paymentsApi.list({ page, limit: 20 }),
        paymentsApi.outstanding(),
      ]);
      setData(list.data);
      setDue(outstanding.data);
    } catch (err) {
      toast.error('Could not load payments', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, toast]);

  useEffect(() => { load(); }, [load]);

  // A Xendit callback settling an order should show up here immediately.
  useOrderEvents((event) => {
    if (event === 'payment:received' || event === 'order:paid') load();
  });

  useEffect(() => { document.title = 'Payments · UrbanClean'; }, []);

  if (loading) return <Loading label="Loading payments" />;

  const byMode = data.totals.reduce((acc, t) => ({ ...acc, [t._id]: t }), {});
  const collected = data.totals.reduce((sum, t) => sum + t.amount, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Payments</h1>
          <div className="sub">Collections, online captures and outstanding dues</div>
        </div>
      </div>

      <div className="grid grid-4 mb">
        <StatCard tone="green" label="Collected" value={money(collected)} sub={`${data.meta?.total || 0} transaction(s)`} />
        <StatCard tone="blue" label="Online (Xendit)" value={money(byMode.online?.amount || 0)} sub={`${byMode.online?.count || 0} payment(s)`} />
        <StatCard tone="navy" label="Cash" value={money(byMode.cod?.amount || 0)} sub={`${byMode.cod?.count || 0} collection(s)`} />
        <StatCard tone="amber" label="Outstanding" value={money(due.totalDue)} sub={`${due.orders.length} order(s) owing`} />
      </div>

      <div className="toolbar">
        <button className={`btn btn-sm ${tab === 'collected' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('collected')}>
          Transactions
        </button>
        <button className={`btn btn-sm ${tab === 'due' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('due')}>
          Outstanding ({due.orders.length})
        </button>
      </div>

      {tab === 'collected' ? (
        <div className="card">
          {data.payments.length === 0 ? <Empty title="No payments yet" /> : (
            <>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr><th>Order</th><th>Customer</th><th>Mode</th><th>Status</th><th className="right">Amount</th><th>When</th></tr>
                  </thead>
                  <tbody>
                    {data.payments.map((p) => (
                      <tr key={p._id}>
                        <td className="mono strong">{p.orderNumber}</td>
                        <td>
                          {p.user?.name || '—'}
                          <div className="muted" style={{ fontSize: 12 }}>{p.user?.mobile}</div>
                        </td>
                        <td>
                          <span className="badge badge-gray">{p.mode?.toUpperCase()}</span>
                          {p.gatewayPaymentChannel ? (
                            <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{p.gatewayPaymentChannel}</div>
                          ) : null}
                        </td>
                        <td>
                          <span className={`badge badge-${p.status === 'success' ? 'green' : p.status === 'pending' ? 'amber' : 'red'}`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="right strong">{money(p.amount)}</td>
                        <td className="muted nowrap">{dateTime(p.paidAt || p.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination meta={data.meta} onPage={setPage} />
            </>
          )}
        </div>
      ) : (
        <div className="card">
          {due.orders.length === 0 ? <Empty title="Nothing outstanding" body="Every order is fully paid." /> : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>Order</th><th>Customer</th><th className="right">Total</th><th className="right">Paid</th><th className="right">Due</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {due.orders.map((o) => (
                    <tr key={o._id}>
                      <td><Link to={`/orders/${o._id}`} className="mono strong">{o.orderNumber}</Link></td>
                      <td>
                        {o.customerName || '—'}
                        <div className="muted" style={{ fontSize: 12 }}>{o.customerMobile}</div>
                      </td>
                      <td className="right">{money(o.total)}</td>
                      <td className="right muted">{money(o.amountPaid)}</td>
                      <td className="right strong" style={{ color: 'var(--red)' }}>{money(o.total - o.amountPaid)}</td>
                      <td><span className="badge badge-amber">{o.paymentStatus}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
